import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;

export interface QuantifiedPlan {
  id: number;
  status: 'Open' | 'Closed';
  /** Only one plan per service area may be active; quantified clones use this plan's contract year. */
  isActive?: boolean;
  year: number;
  startDate: string | Date;
  endDate: string | Date;
  annualTarget: number;
  annualAdjustment: number;
  serviceAreaId: number;
  serviceArea?: ServiceArea;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface ServiceArea {
  id: number;
  name: string;
  code?: string;
  foremanAreas?: ForemanArea[];
  quantifiedPlans?: QuantifiedPlan[];
}

export interface ForemanArea {
  id: number;
  name: string;
  code?: string;
  serviceAreaId: number;
  serviceArea: ServiceArea;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  phone?: string | null;
  /** Fax for public WordPress map (stored as `email` in DB). */
  email?: string | null;
  /** When true, not included in GET /api/wordpress/service-map. */
  omitFromWordpressMap?: boolean;
  contractAreas?: ContractArea[];
}

export interface Asset {
  id: number;
  name: string;
  description?: string;
  assetType: string;
  assetNumber: string;
  contractAreaId: number;
}

export interface ContractArea {
  id: number;
  name: string;
  code?: string;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  phone?: string | null;
  /** Fax number (legacy field; public map uses Foreman Area). */
  email?: string | null;
  /** Legacy; public map uses ForemanArea.omitFromWordpressMap. */
  omitFromWordpressMap?: boolean;
  foremanAreas?: ForemanArea[]; // Many-to-many relationship
  assets?: Asset[];
  assetsCount?: number;
}

class AreasService {
  private baseUrl = `${BASE_URL}`;

  // ========== SERVICE AREAS ==========
  async getServiceAreas(): Promise<ServiceArea[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/service-areas`);
      // A proxy timeout returns an HTML page with 200/503; never hand that on as a list.
      if (!Array.isArray(response.data)) throw new Error('Invalid service areas response from server');
      return response.data;
    } catch (error) {
      console.error('Error fetching service areas:', error);
      throw error;
    }
  }

  async getServiceArea(id: number): Promise<ServiceArea> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/service-areas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching service area:', error);
      throw error;
    }
  }

  async createServiceArea(serviceAreaData: Omit<ServiceArea, 'id' | 'foremanAreas'>): Promise<ServiceArea> {
    try {
      const response = await axios.post(`${this.baseUrl}/areas/service-areas`, serviceAreaData);
      return response.data;
    } catch (error) {
      console.error('Error creating service area:', error);
      throw error;
    }
  }

  async updateServiceArea(id: number, serviceAreaData: Partial<ServiceArea>): Promise<ServiceArea> {
    try {
      const response = await axios.put(`${this.baseUrl}/areas/service-areas/${id}`, serviceAreaData);
      return response.data;
    } catch (error) {
      console.error('Error updating service area:', error);
      throw error;
    }
  }

  async deleteServiceArea(id: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/areas/service-areas/${id}`);
    } catch (error) {
      console.error('Error deleting service area:', error);
      throw error;
    }
  }

  // ========== FOREMAN AREAS ==========
  async getForemanAreas(): Promise<ForemanArea[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/foreman-areas`);
      if (!Array.isArray(response.data)) throw new Error('Invalid foreman areas response from server');
      return response.data;
    } catch (error) {
      console.error('Error fetching foreman areas:', error);
      throw error;
    }
  }

  async getContractAreas(foremanAreaId: number): Promise<ContractArea[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/foreman-areas/${foremanAreaId}/contract-areas`);
      return response.data;
    } catch (error) {
      console.error('Error fetching contract areas:', error);
      throw error;
    }
  }

  async getContractAreasByServiceArea(serviceAreaId: number): Promise<ContractArea[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/service-areas/${serviceAreaId}/contract-areas`);
      return response.data;
    } catch (error) {
      console.error('Error fetching contract areas by service area:', error);
      throw error;
    }
  }

  async createForemanArea(
    areaData: Omit<ForemanArea, 'id' | 'serviceArea' | 'contractAreas'> & { contractAreaIds?: number[] }
  ): Promise<ForemanArea> {
    try {
      const response = await axios.post(`${this.baseUrl}/foreman-areas`, areaData);
      return response.data;
    } catch (error) {
      console.error('Error creating foreman area:', error);
      throw error;
    }
  }

  async getForemanArea(id: number): Promise<ForemanArea> {
    try {
      const response = await axios.get(`${this.baseUrl}/foreman-areas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching foreman area:', error);
      throw error;
    }
  }

  async updateForemanArea(
    id: number,
    foremanAreaData: Partial<ForemanArea> & { contractAreaIds?: number[] }
  ): Promise<ForemanArea> {
    try {
      const response = await axios.put(`${this.baseUrl}/foreman-areas/${id}`, foremanAreaData);
      return response.data;
    } catch (error) {
      console.error('Error updating foreman area:', error);
      throw error;
    }
  }

  async deleteForemanArea(id: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/foreman-areas/${id}`);
    } catch (error) {
      console.error('Error deleting foreman area:', error);
      throw error;
    }
  }

  // ========== CONTRACT AREAS ==========

  async getAllContractAreas(): Promise<ContractArea[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/contract-areas`);
      const raw = response.data;
      if (!Array.isArray(raw)) {
        console.error('Unexpected contract areas response (expected array):', raw);
        throw new Error('Invalid contract areas response from server');
      }

      return raw.map((area: ContractArea & { assets?: Asset[] }) => {
        const { assets, ...rest } = area;
        return {
          ...rest,
          code: area.code || '',
          assets: undefined,
          // The list sends assetsCount (W-M17) and no longer embeds every asset; reading only
          // assets?.length here turned every count into 0 and enabled Delete on areas with assets.
          assetsCount: area.assetsCount ?? assets?.length ?? 0
        };
      });
    } catch (error) {
      console.error('Error fetching all contract areas:', error);
      throw error;
    }
  }

  // Debug endpoint for testing
  async debugContractAreas(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/debug/contract-areas`);
      console.log('Debug Contract Areas Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching debug contract areas:', error);
      throw error;
    }
  }

  async getContractArea(id: number): Promise<ContractArea> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/contract-areas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching contract area:', error);
      throw error;
    }
  }

  async createContractArea(contractAreaData: Omit<ContractArea, 'id' | 'foremanAreas' | 'assets'> & { foremanAreaIds: number[] }): Promise<ContractArea> {
    try {
      const response = await axios.post(`${this.baseUrl}/areas/contract-areas`, contractAreaData);
      return response.data;
    } catch (error) {
      console.error('Error creating contract area:', error);
      throw error;
    }
  }

  async updateContractArea(id: number, contractAreaData: Partial<Omit<ContractArea, 'foremanAreas' | 'assets'>> & { foremanAreaIds?: number[] }): Promise<ContractArea> {
    try {
      const response = await axios.put(`${this.baseUrl}/areas/contract-areas/${id}`, contractAreaData);
      return response.data;
    } catch (error) {
      console.error('Error updating contract area:', error);
      throw error;
    }
  }

  async deleteContractArea(id: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/areas/contract-areas/${id}`);
    } catch (error) {
      console.error('Error deleting contract area:', error);
      throw error;
    }
  }

  // ========== QUANTIFIED PLANS ==========
  async getQuantifiedPlans(serviceAreaId: number): Promise<QuantifiedPlan[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/service-areas/${serviceAreaId}/quantified-plans`);
      return response.data;
    } catch (error) {
      console.error('Error fetching quantified plans:', error);
      throw error;
    }
  }

  async getQuantifiedPlan(id: number): Promise<QuantifiedPlan> {
    try {
      const response = await axios.get(`${this.baseUrl}/areas/quantified-plans/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching quantified plan:', error);
      throw error;
    }
  }

  async createQuantifiedPlan(data: Omit<QuantifiedPlan, 'id' | 'serviceArea' | 'createdAt' | 'updatedAt'>): Promise<QuantifiedPlan> {
    try {
      const response = await axios.post(`${this.baseUrl}/areas/quantified-plans`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating quantified plan:', error);
      throw error;
    }
  }

  async updateQuantifiedPlan(id: number, data: Partial<Omit<QuantifiedPlan, 'id' | 'serviceArea' | 'createdAt' | 'updatedAt'>>): Promise<QuantifiedPlan> {
    try {
      const response = await axios.put(`${this.baseUrl}/areas/quantified-plans/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating quantified plan:', error);
      throw error;
    }
  }

  async deleteQuantifiedPlan(id: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/areas/quantified-plans/${id}`);
    } catch (error) {
      console.error('Error deleting quantified plan:', error);
      throw error;
    }
  }
}

export const areasService = new AreasService();
